/**
 * Core Layer - Environment Parsing Utilities
 * Pure utility functions for parsing environment variable values
 */

// ============================================================================
// PARSING FUNCTIONS
// ============================================================================

/**
 * Parse a string to number (returns 0 if not set or invalid)
 */
export const parseNumber = (value: string | undefined): number => {
  if (!value) return 0;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Parse a string to boolean (returns false if not set)
 * Accepts: true/false, 1/0, yes/no, on/off
 */
export const parseBoolean = (value: string | undefined): boolean => {
  if (!value) return false;
  const lowered = value.toLowerCase();
  return ["true", "1", "yes", "on"].includes(lowered);
};

/**
 * Parse comma-separated string to array (returns empty array if not set)
 */
export const parseArray = (
  value: string | undefined,
  separator: string = ","
): string[] => {
  if (!value) return [];
  return value
    .split(separator)
    .map((s) => s.trim())
    .filter(Boolean);
};

/**
 * Get string value or empty string
 */
export const parseString = (value: string | undefined): string => {
  return value ?? "";
};

// ============================================================================
// BACKEND URL RESOLUTION
// ============================================================================

/**
 * Settings for useLocalBackend configuration
 */
export type UseLocalBackendSetting = "true" | "false" | "auto" | "";

/**
 * Resolve the useLocalBackend setting to a boolean
 * - "true": Always use local backend
 * - "false": Always use production backend
 * - "auto" | "": Use local in development, production otherwise
 */
export const resolveUseLocalBackend = (
  setting: string | undefined,
  isDevelopment: boolean
): boolean => {
  switch (setting) {
    case "true":
      return true;
    case "false":
      return false;
    case "auto":
    default:
      return isDevelopment;
  }
};

/**
 * Get the resolved API base URL based on configuration
 * Priority: 1) Direct API_BASE_URL override, 2) Local/Production based on setting
 */
export const resolveApiBaseUrl = (
  directUrl: string | undefined,
  localUrl: string | undefined,
  productionUrl: string | undefined,
  useLocalBackend: boolean
): string => {
  // Priority 1: Direct API base URL override
  if (directUrl) {
    return directUrl;
  }

  // Priority 2: Use local or production based on setting
  return useLocalBackend ? (localUrl ?? "") : (productionUrl ?? "");
};
